import React, { useEffect, useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import {
  LayoutDashboard, FileText, Sliders, Search, ClipboardCheck, ShieldCheck,
  UserCheck, Eye, Monitor, MessageSquare, Radar, Activity, ShieldAlert, GitBranch, Network
} from 'lucide-react';
import { getApiKey } from '@/lib/api';
import type { ViewId } from './CommandCenter';

interface SidebarProps {
  currentView: ViewId;
  onViewChange: (view: ViewId) => void;
}

// Phase 3f (2026-06-21): tier-visibility wired at the Command Center entrypoint.
// Each nav item carries an optional matrix featureId; the Sidebar fetches the
// current user's tier via /api/tiers/me on mount and hides items whose required
// tier exceeds the user's tier. Items without a featureId stay visible at all
// tiers (graceful default for views that pre-date the matrix or are universal).
//
// Every nav item now carries a matrix featureId: Phase 5b5 mapped the once-unmapped
// discovery/security/desktop/chat/threats, and threat-intel was mapped to
// dashboard.threat-intel on 2026-08-27. The graceful-default branch in the render below
// only applies while the /api/tiers/me fetch is in flight or has failed -- it is a
// loading/failure fallback, NOT a set of intentionally ungated views.
//
// Each NAV item also carries a one-line tooltip (title) so a first-time operator
// hovering on a view label learns what the view actually does before clicking.
// Closes the value-conveyance gap that surfaced in the 2026-05-30 Stripe-Erdos
// audit (jargon-label views with no explainer).
const NAV_ITEMS: { id: ViewId; label: string; icon: React.ElementType; hint: string; featureId?: string }[] = [
  { id: 'fleet',          label: 'Fleet Overview',  icon: LayoutDashboard, hint: 'Every governed agent at a glance: tier, status, last action, halt state', featureId: 'dashboard.fleet' },
  { id: 'audit',          label: 'Audit Trail',     icon: FileText,        hint: 'Tamper-evident chain of every governance decision; export for regulator review', featureId: 'dashboard.audit' },
  { id: 'steer',          label: 'Steer Console',   icon: Sliders,         hint: 'Manual policy override + per-agent quarantine / release (Operator tier and up)', featureId: 'dashboard.steer' },
  { id: 'discovery',      label: 'Discovery',       icon: Search,          hint: 'Find AI agents your org runs without knowing it; map frameworks + connectors', featureId: 'dashboard.discovery' },
  { id: 'compliance',     label: 'Compliance',      icon: ClipboardCheck,  hint: 'Self-assessed readiness against SOC 2, EU AI Act, HIPAA, GDPR, ISO 27001, PCI DSS, NIST 800-53', featureId: 'dashboard.compliance' },
  { id: 'security',       label: 'Security Status', icon: ShieldCheck,     hint: 'Active threats, recent halts, anomaly alerts, kill-switch state', featureId: 'dashboard.security' },
  { id: 'gdpr',           label: 'GDPR Rights',     icon: UserCheck,       hint: 'Article 22 data-subject requests: explain, human review, contest, access', featureId: 'dashboard.gdpr' },
  { id: 'explainability', label: 'Explainability',  icon: Eye,             hint: 'EU AI Act Article 13: per-decision rationale, tier basis, anomaly trigger', featureId: 'dashboard.explainability' },
  { id: 'desktop',        label: 'Desktop Daemon',  icon: Monitor,         hint: 'Local agentomy-edge instances: per-machine governance + air-gap support', featureId: 'dashboard.desktop' },
  { id: 'chat',           label: 'Chat',            icon: MessageSquare,   hint: 'Governed chat: every message scored against content scan + compliance + IP protection inline; supports slash-commands for diagnostic queries', featureId: 'dashboard.chat' },
  { id: 'threats',        label: 'Live Threat Watch', icon: Radar,        hint: 'Live OSINT-detected governance threats: vulnerabilities, exploits, supply-chain events tracked from public security feeds', featureId: 'dashboard.threats' },
  { id: 'threat-intel',   label: 'Threat Intelligence', icon: Network,   hint: 'Cross-agent threat-intelligence graph: anonymized attack signatures your fleet has already seen, which are reused across agents, and (operators) the fleet-wide aggregate + moat metric. Intra-tenant Phase 1.', featureId: 'dashboard.threat-intel' },
  { id: 'trust-anomaly',  label: 'Trust Anomaly',   icon: Activity,        hint: 'Continuous trust-network scan: signature mismatches, behavior drift, trust-chain anomalies across the agent fleet (CODE-002 mounted 2026-06-19, 5-min background scan)', featureId: 'dashboard.trust-anomaly' },
  { id: 'ipi-defense',    label: 'IPI Defense',     icon: ShieldAlert,     hint: 'Indirect-prompt-injection + agentjacking verdict gate: preview the verdict on a candidate payload + source-context before letting an agent act on it (Phase 1.1 + 1.5 + 1.8)', featureId: 'dashboard.ipi-defense' },
  { id: 'workflow-governance', label: 'Workflow Governance', icon: GitBranch, hint: 'Per-workflow governance for n8n workflows running @agentomy/n8n-nodes: verdict counts, attestation chain length, isolation-key activity. Phase 2 dashboard.workflow-governance feature (Analyst tier).', featureId: 'dashboard.workflow-governance' },
];

async function fetchMyTierFeatures(): Promise<Set<string>> {
  // /api/tiers/me returns the full feature list unlocked at the caller's tier.
  // We use the feature IDs to filter sidebar items -- same source-of-truth as
  // TierFeatureGrid + the runtime tier-gate middleware (Phase 3a-3f).
  try {
    const key = getApiKey();
    const headers: Record<string, string> = {};
    if (key) {headers['x-api-key'] = key;}
    const res = await fetch('/api/tiers/me', { headers });
    if (!res.ok) {return new Set();}
    const data = await res.json();
    const features = Array.isArray(data?.features) ? data.features : [];
    return new Set(features.map((f: { id: string }) => f.id));
  } catch {
    return new Set();
  }
}

export function Sidebar({ currentView, onViewChange }: SidebarProps) {
  // Phase 3f (2026-06-21): per-tier visibility. Fetch unlocked feature IDs on
  // mount; items with a featureId not in the unlocked set are hidden. Items
  // without a featureId stay visible at all tiers. While loading, all items
  // are visible (graceful degradation -- never hide due to fetch failure).
  const [unlockedFeatures, setUnlockedFeatures] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyTierFeatures().then((set) => {
      if (!cancelled) {setUnlockedFeatures(set);}
    });
    return () => { cancelled = true; };
  }, []);

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.featureId) {return true;} // graceful default for unmapped items
    if (unlockedFeatures === null) {return true;} // loading state: show all
    if (unlockedFeatures.size === 0) {return true;} // fetch failed: show all
    return unlockedFeatures.has(item.featureId);
  });

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-[220px] bg-[#1C1917] border-r border-[#2E2A26] flex flex-col">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-[#2E2A26]">
        <span className="text-lg font-bold tracking-tight">
          <span style={{ color: GovernanceColors.primary }}>Agentomy</span>
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3">
        {visibleItems.map(({ id, label, icon: Icon, hint }) => {
          const active = currentView === id;
          return (
            <button
              key={id}
              onClick={() => onViewChange(id)}
              aria-current={active ? 'page' : undefined}
              title={hint}
              aria-label={`${label} -- ${hint}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                active
                  ? 'text-[#F5F0E8] bg-[#292524]'
                  : 'text-[#A8A29E] hover:text-[#F5F0E8] hover:bg-[#292524]/50 border-transparent'
              }`}
              style={active ? { borderColor: GovernanceColors.primary } : undefined}
            >
              <Icon aria-hidden="true" className="w-4 h-4" style={active ? { color: GovernanceColors.primary } : undefined} />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-[#2E2A26]">
        <span className="text-xs text-[#8F857B]">v0.31.0</span>
      </div>
    </aside>
  );
}
